'use client'

import { brl, parseValor, sanitizeMoneyInput } from './format'
import { Icon } from './icon'

export interface ItemLista {
  id: number
  desc: string
  valor: string
}

interface ListaItensProps {
  label: string
  items: ItemLista[]
  onAdd: (desc?: string) => void
  onRemove: (id: number) => void
  onUpdate: (id: number, field: 'desc' | 'valor', val: string) => void
  /** Chips de atalho para itens comuns (somem quando já usados). */
  sugestoes?: string[]
  descPlaceholder?: string
  addLabel?: string
  hint?: string
}

/**
 * Lista dinâmica de itens { descrição, valor } com soma automática e
 * chips de sugestão. Reusada para custos, entradas e saídas.
 */
export function ListaItens({
  label,
  items,
  onAdd,
  onRemove,
  onUpdate,
  sugestoes = [],
  descPlaceholder = 'Ex.: Internet',
  addLabel = 'Adicionar outro item',
  hint,
}: ListaItensProps) {
  const total = items.reduce((s, c) => s + parseValor(c.valor), 0)
  const jaUsadas = items.map((c) => c.desc.trim().toLowerCase())
  const disponiveis = sugestoes.filter((s) => !jaUsadas.includes(s.toLowerCase()))

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm text-ink">{label}</span>
        <span className="font-mono text-[12.5px] text-ink-mute tabular-nums">{brl(total)}</span>
      </div>

      {disponiveis.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {disponiveis.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onAdd(s)}
              className="flex items-center gap-1.5 rounded-full border border-brand-tint-strong bg-brand-tint px-3 py-[5px] text-[11.5px] text-brand hover:bg-brand-tint-strong"
            >
              <Icon name="plus" size={12} /> {s}
            </button>
          ))}
        </div>
      )}

      {items.map((c) => (
        <div key={c.id} className="mb-2 flex items-center gap-1.5">
          <input
            value={c.desc}
            placeholder={descPlaceholder}
            onChange={(e) => onUpdate(c.id, 'desc', e.target.value)}
            className="h-[42px] min-w-0 flex-1 rounded-[10px] border border-line bg-surface px-3 text-sm text-ink outline-none focus:ring-1 focus:ring-inset focus:ring-brand"
          />
          <div className="flex h-[42px] w-[110px] items-center overflow-hidden rounded-[10px] border border-line bg-surface focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand">
            <span className="flex h-full items-center border-r border-line-soft px-2 font-mono text-xs text-ink-faint">
              R$
            </span>
            <input
              inputMode="decimal"
              value={c.valor}
              placeholder="0,00"
              onChange={(e) => onUpdate(c.id, 'valor', sanitizeMoneyInput(e.target.value))}
              className="h-full min-w-0 flex-1 bg-transparent px-2 font-mono text-sm text-ink outline-none placeholder:text-ink-faint"
            />
          </div>
          <button
            type="button"
            aria-label="Remover item"
            onClick={() => onRemove(c.id)}
            className="flex h-[42px] w-9 shrink-0 items-center justify-center rounded-[10px] border border-line bg-background text-ink-faint hover:border-neg hover:text-neg"
          >
            <Icon name="x" size={15} />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onAdd()}
        className="mt-0.5 flex h-10 w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-line bg-brand-faint text-brand hover:border-brand"
      >
        <Icon name="plus" size={15} />
        <span className="text-[13px] font-medium">{addLabel}</span>
      </button>

      {hint && <p className="mt-2 text-[11.5px] text-ink-faint">{hint}</p>}
    </div>
  )
}
